import { DEFAULT_LAYOUT, SECTION_NAMES } from './experienceModel'
import { componentInstanceName } from './nativeSportsbookManifest'
import { inspectionFieldLabel } from './nativeInspectionFields'

const SEMANTIC_VERSION_PATTERN = /^\d+\.\d+\.\d+$/
const HUB_SCREEN_PATTERN = /^hub:[0-9a-f-]{36}$/

export function previewPairingURL(response, origin) {
  const server = new URL('/api/native-preview/v1/devices', origin)
  if (response.device.platform === 'android' && ['localhost', '127.0.0.1'].includes(server.hostname)) server.hostname = '10.0.2.2'
  const scheme = response.device.channel === 'preview' ? 'wagergames-fixture-preview' : 'wagergames-staging-preview'
  const params = new URLSearchParams({ server: server.toString(), device: response.device.id, token: response.token })
  return `${scheme}://preview?${params}`
}

export function supportsDeviceInspection(device) {
  const value = String(device.report?.app_version || '')
  if (!SEMANTIC_VERSION_PATTERN.test(value)) return false
  const version = value.split('.').map(Number)
  return version.length === 3 && version.every((part) => Number.isInteger(part) && part >= 0) && (version[0] > 0 || version[1] > 4 || (version[1] === 4 && version[2] >= 1))
}

export function hasInspectableScreen(device) {
  return Boolean(device.report?.screen === 'home' || device.report?.screen?.startsWith('event:') || HUB_SCREEN_PATTERN.test(device.report?.screen || ''))
}

export function resolveDeviceInspection(device, selected, draft) {
  const report = device?.report
  const pick = report?.selection
  const target = device?.draft_preview || device?.requested
  if (!pick || !device.inspection_enabled || pick.inspection_revision !== device.inspection_revision || device.status !== 'rendered' || !target) return null
  if ((report.preview_snapshot_id || null) !== (device.draft_preview?.snapshot_id || null)) return null
  if (!selected || target.manifest_id !== (selected.manifest_id || selected.document?.manifest_id) || target.tenant_id !== draft.tenant_id) {
    return { error: 'Load a revision of the experience shown on this phone before inspecting it.' }
  }
  if (report.manifest_version !== target.manifest_version || report.content_digest !== target.content_digest || report.tenant_id !== target.tenant_id || report.environment !== target.environment || report.channel !== target.channel || report.phase !== device.rehearsal_phase) return null
  const screen = draft.screens.find((item) => item.id === pick.screen_id)
  const matchesScreen = screen && ((report.screen === 'home' && screen.route === '/home') || (report.screen === `hub:${screen.id}` && screen.route === `/hub/${screen.id}` && !pick.scene_id))
  if (matchesScreen) {
    if (pick.scene_id && !draft.experience?.home_scenes.some((item) => item.id === pick.scene_id)) return { error: 'The scene shown on this phone was removed from the current draft.' }
    const component = screen.components.find((item) => item.id === pick.component_id)
    if (component) {
      const card = component.cards?.find((item) => item.id === pick.card_id)
      if (pick.card_id && !card) return { error: 'This card was removed from the current draft.' }
      const fieldLabel = inspectionFieldLabel(card, pick.field)
      if (pick.field && !fieldLabel) return { error: 'This field is no longer available in the current draft. Tap the card to inspect its current content.' }
      return { selection: { surface: 'home', screenId: screen.id, componentId: component.id, cardId: card?.id ?? null, sceneId: pick.scene_id ?? null, field: pick.field ?? null, fieldSelectionId: pick.field ? pick.id : null }, label: fieldLabel ? `${card.headline} · ${fieldLabel}` : card?.headline || componentInstanceName(component) }
    }
    const navigation = Object.entries(screen.navigation || {}).find(([, bar]) => bar.id === pick.component_id)
    if (navigation && !pick.field) return { selection: { surface: 'home', screenId: screen.id, componentId: `navigation:${navigation[0]}`, cardId: null }, label: navigation[0] === 'sports' ? 'Sports navigation' : 'Quick filters' }
    return { error: 'This section was removed from the current draft.' }
  }
  const templates = draft.experience?.event_templates || []
  const template = templates.find((item) => item.id === pick.template_id && item.screen_id === pick.screen_id)
  if (!report.screen?.startsWith('event:') || !template) return { error: 'The template shown on this phone is not in the current draft.' }
  const scenes = templates.flatMap((item) => Object.entries(item.scenes || {}))
  const scene = scenes.find(([, item]) => item.id === pick.scene_id)
  if (pick.scene_id && !scene) return { error: 'The scene shown on this phone was removed from the current draft.' }
  const sections = [...templates.flatMap((item) => item.layout.sections || []), ...scenes.flatMap(([, item]) => item.layout.sections || []), ...DEFAULT_LAYOUT.sections]
  const section = sections.find((item) => item.id === pick.component_id)
  if (!section) return { error: 'This event section was removed from the current draft.' }
  return { selection: { surface: 'events', templateId: template.id, eventId: report.screen.slice(6), layer: scene?.[0] || 'base', section: section.type, phase: scene?.[0] || report.phase || 'pre_match' }, label: `${template.name} · ${SECTION_NAMES[section.type]}` }
}

/** Phones paired to this workspace that have not expired. */
export function activePreviewDevices(workspace) {
  return (workspace.previewDevicesQuery?.data?.devices || []).filter((device) => device.status !== 'expired')
}
